import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { fetchStaatidDrivlinjer, UNKNOWN_ERROR } from './api'
import type { StaatidDrivlinjerResponse, StaatidParams } from './api'
import DrivlinjerChart from './DrivlinjerChart'
import DrivlinjerTable from './DrivlinjerTable'
import StaatidFilterBar, { EMPTY_LOCAL_FILTERS } from './StaatidFilterBar'
import type { StaatidLocalFilters } from './StaatidFilterBar'
import FilterSlot, { useLocalFilter } from '../FilterSlot'
import { useMaxDataDate } from '../../../hooks/useMaxDataDate'

/** Ståtid per drivlinje over time (#800): one median line per drivlinje and
 *  a summary table, under the same filters as the other ståtid tabs. */
export default function DrivlinjerTab() {
  const { t } = useTranslation('staatid')
  const { from, to, selgerPostnummer } = useBruktbilFilters()
  const maxDataDate = useMaxDataDate()

  const [localDraft, setLocalDraft, localFilters] =
    useLocalFilter<StaatidLocalFilters>('staatid', EMPTY_LOCAL_FILTERS)
  const [data, setData] = useState<StaatidDrivlinjerResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filters: StaatidParams = { from, to, selgerPostnummer, ...localFilters }
  const filterKey = `${from}|${to}|${selgerPostnummer ?? ''}|${JSON.stringify(localFilters)}`

  useLatest(alive => {
    setLoading(true)
    setError(null)
    fetchStaatidDrivlinjer(filters)
      .then(r => { if (alive()) setData(r.data) })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : UNKNOWN_ERROR) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [filterKey])

  if (error) {
    return (
      <div className="card p-4 text-red-600 dark:text-red-400 text-sm">
        {t('common.loadError', { error: error === UNKNOWN_ERROR ? t('common.unknownError') : error })}
      </div>
    )
  }

  const rows = data?.rows ?? []
  return (
    <div className="space-y-4" data-testid="staatid-drivlinjer-tab">
      <FilterSlot>
        <StaatidFilterBar value={localDraft} onChange={setLocalDraft} />
      </FilterSlot>
      {!loading && data && data.total === 0 ? (
        <div className="card p-4 text-sm text-gray-500 dark:text-gray-400" data-testid="staatid-drivlinjer-empty">
          {t('common.noData')}
        </div>
      ) : (
        <>
          <DrivlinjerChart rows={rows} loading={loading} maxDataDate={maxDataDate} />
          <DrivlinjerTable
            rows={rows}
            total={data?.total ?? 0}
            loading={loading}
            exportMeta={{ dateRange: `${from} – ${to}` }}
          />
        </>
      )}
    </div>
  )
}
